import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { controlApi } from "./api";
import type { NexusRoleListItem } from "./types";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";
import { cn } from "@/lib/utils/cn";

export function RolesScreen() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();
  const { push } = useToast();

  const rolesQuery = useQuery({
    queryKey: ["nexus-control", "roles"],
    queryFn: () => controlApi.listRoles(),
  });

  const catalogQuery = useQuery({
    queryKey: ["nexus-control", "permissions-catalog"],
    queryFn: () => controlApi.permissionsCatalog(),
  });

  const roles = rolesQuery.data?.roles ?? [];
  const selected = useMemo(
    () => roles.find((r) => r.id === selectedId) ?? null,
    [roles, selectedId],
  );

  // Auto-select primero si nada seleccionado
  useEffect(() => {
    if (roles.length > 0 && selectedId === null) {
      setSelectedId(roles[0].id);
    }
  }, [roles, selectedId]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["nexus-control", "roles"] });
    qc.invalidateQueries({ queryKey: ["nexus-control", "users"] });
  };

  const create = useMutation({
    mutationFn: controlApi.createRole,
    onSuccess: (role) => {
      push({ kind: "success", message: `Rol "${role.name}" creado.` });
      invalidate();
      setCreateOpen(false);
      setSelectedId(role.id);
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al crear rol." }),
  });

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Nexus Control · Roles</p>
          <h1 className="nx-h1">Roles y permisos</h1>
          <p className="text-sm text-fg-muted mt-1">
            7 roles preset + custom. Editá los permisos de cualquier rol salvo
            Owner (intocable). Los cambios en permisos se propagan inmediatamente
            a los usuarios del rol (cache 60s).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCreateOpen(true)} className="nx-btn-primary">
            <Plus size={14} /> Crear rol custom
          </button>
          <button
            onClick={() => rolesQuery.refetch()}
            disabled={rolesQuery.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw size={14} className={rolesQuery.isFetching ? "animate-spin" : ""} />
            Actualizar
          </button>
        </div>
      </header>

      {rolesQuery.isLoading ? (
        <LoadingBlock label="Cargando roles…" />
      ) : rolesQuery.isError ? (
        <ErrorBlock onRetry={() => rolesQuery.refetch()} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
          {/* Sidebar de roles */}
          <aside className="nx-card p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
            <h2 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-3">
              Roles ({roles.length})
            </h2>
            <div className="flex flex-col gap-1.5">
              {roles.map((role) => (
                <RoleRow
                  key={role.id}
                  role={role}
                  selected={role.id === selectedId}
                  onSelect={() => setSelectedId(role.id)}
                />
              ))}
            </div>
          </aside>

          {/* Editor */}
          <main>
            {selected ? (
              <RoleEditor
                key={selected.id}
                role={selected}
                groups={catalogQuery.data?.groups ?? {}}
                catalogLoading={catalogQuery.isLoading}
                onDeleted={() => setSelectedId(null)}
              />
            ) : (
              <div className="nx-card p-8 text-center text-fg-subtle text-sm">
                Seleccioná un rol.
              </div>
            )}
          </main>
        </div>
      )}

      {createOpen && (
        <CreateRoleModal
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => create.mutate(data)}
          submitting={create.isPending}
          groups={catalogQuery.data?.groups ?? {}}
        />
      )}
    </div>
  );
}

function RoleRow({
  role,
  selected,
  onSelect,
}: {
  role: NexusRoleListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={cn(
        "text-left rounded-md px-2.5 py-2 transition-colors border",
        selected ? "bg-accent-soft border-accent" : "border-transparent hover:bg-bg-hover",
      )}
    >
      <div className="flex items-center gap-1.5 mb-0.5">
        {role.isSystem ? (
          <Lock size={11} className="text-fg-subtle" />
        ) : (
          <ShieldCheck size={11} className="text-info" />
        )}
        <span className="text-xs font-medium text-fg truncate flex-1">{role.name}</span>
      </div>
      <div className="flex items-center gap-2 text-2xs text-fg-subtle">
        <span>{role.userCount} {role.userCount === 1 ? "user" : "users"}</span>
        <span>·</span>
        <span>{role.permissions.length} {role.permissions.length === 1 ? "permiso" : "permisos"}</span>
        {role.permissions.includes("*") && <span className="text-accent font-semibold">★ Owner</span>}
      </div>
    </button>
  );
}

function RoleEditor({
  role,
  groups,
  catalogLoading,
  onDeleted,
}: {
  role: NexusRoleListItem;
  groups: Record<string, string[]>;
  catalogLoading: boolean;
  onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const { push } = useToast();

  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [permissions, setPermissions] = useState<Set<string>>(new Set(role.permissions));

  useEffect(() => {
    setName(role.name);
    setDescription(role.description);
    setPermissions(new Set(role.permissions));
  }, [role.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isOwner = role.slug === "owner";
  const dirty =
    !isOwner &&
    (name !== role.name ||
      description !== role.description ||
      permissions.size !== role.permissions.length ||
      [...permissions].some((p) => !role.permissions.includes(p)));

  const save = useMutation({
    mutationFn: () =>
      controlApi.updateRole(role.id, {
        name: name !== role.name ? name : undefined,
        description: description !== role.description ? description : undefined,
        permissions: Array.from(permissions),
      }),
    onSuccess: () => {
      push({ kind: "success", message: "Rol actualizado. Cambios propagados a usuarios." });
      qc.invalidateQueries({ queryKey: ["nexus-control", "roles"] });
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al guardar." }),
  });

  const deleteMut = useMutation({
    mutationFn: () => controlApi.deleteRole(role.id),
    onSuccess: () => {
      push({ kind: "success", message: "Rol eliminado." });
      qc.invalidateQueries({ queryKey: ["nexus-control", "roles"] });
      onDeleted();
    },
    onError: (err: any) =>
      push({ kind: "error", message: err?.response?.data?.message || "Error al eliminar." }),
  });

  const handleDelete = async () => {
    if (role.isSystem) {
      push({ kind: "error", message: "Los roles del sistema no se pueden eliminar." });
      return;
    }
    if (role.userCount > 0) {
      push({
        kind: "error",
        message: `El rol tiene ${role.userCount} user(s) asignado(s). Revocá los grants primero.`,
      });
      return;
    }
    const ok = await confirmAction({
      title: `Eliminar rol "${role.name}"`,
      message: "Esta acción no es reversible.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (ok === true) deleteMut.mutate();
  };

  const toggle = (perm: string, on: boolean) => {
    setPermissions((s) => {
      const next = new Set(s);
      if (on) next.add(perm);
      else next.delete(perm);
      return next;
    });
  };

  const toggleGroup = (perms: string[], on: boolean) => {
    setPermissions((s) => {
      const next = new Set(s);
      perms.forEach((p) => (on ? next.add(p) : next.delete(p)));
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="nx-card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {role.isSystem ? (
                <span className="nx-badge-neutral">
                  <Lock size={10} className="inline mr-1" />
                  Sistema
                </span>
              ) : (
                <span className="nx-badge-info">Custom</span>
              )}
              {isOwner && (
                <span className="nx-badge-accent">★ Wildcard</span>
              )}
              <span className="text-2xs text-fg-subtle">
                slug: <code className="bg-bg-subtle px-1 rounded">{role.slug}</code>
              </span>
              {dirty && <span className="text-2xs text-warning font-medium">● Sin guardar</span>}
            </div>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isOwner}
              maxLength={128}
              className="nx-input font-semibold text-base mb-2"
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isOwner}
              rows={2}
              placeholder="Descripción del rol"
              className="nx-input resize-y text-xs"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-3 justify-end">
          <span className="text-2xs text-fg-subtle mr-auto">
            {role.userCount} {role.userCount === 1 ? "usuario asignado" : "usuarios asignados"} ·
            actualizado {formatDateTime(role.updatedAt)}
          </span>
          {!role.isSystem && (
            <button
              onClick={handleDelete}
              disabled={deleteMut.isPending}
              className="nx-btn-ghost text-xs text-danger hover:text-danger"
            >
              <Trash2 size={12} /> Eliminar
            </button>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="nx-btn-primary text-xs"
          >
            {save.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Guardar cambios
          </button>
        </div>
      </header>

      {isOwner && (
        <div className="flex items-start gap-2 text-xs p-3 rounded-md border border-accent/40 bg-accent-soft text-fg">
          <AlertTriangle size={14} className="text-accent shrink-0 mt-0.5" />
          <span>
            <strong>Owner</strong> es el rol del wildcard <code>*</code> — tiene
            todos los permisos siempre. No es editable ni eliminable. Si se
            corrompe, el seed lo restaura al próximo boot.
          </span>
        </div>
      )}

      <section className="nx-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-fg">
            Permisos ({permissions.size}{role.isSystem ? "" : ` / ${Object.values(groups).flat().length}`})
          </h3>
          {isOwner && (
            <span className="text-2xs text-fg-subtle italic">
              Owner siempre tiene todos los permisos (<code>*</code>)
            </span>
          )}
        </div>

        {catalogLoading ? (
          <div className="flex items-center justify-center py-8 text-fg-subtle">
            <Loader2 size={14} className="animate-spin mr-2" />
            Cargando catálogo…
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {Object.entries(groups).map(([groupName, perms]) => (
              <PermissionGroup
                key={groupName}
                groupName={groupName}
                perms={perms}
                selected={permissions}
                onToggle={(p, on) => toggle(p, on)}
                onToggleGroup={(on) => toggleGroup(perms, on)}
                disabled={isOwner}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PermissionGroup({
  groupName,
  perms,
  selected,
  onToggle,
  onToggleGroup,
  disabled,
}: {
  groupName: string;
  perms: string[];
  selected: Set<string>;
  onToggle: (perm: string, on: boolean) => void;
  onToggleGroup: (on: boolean) => void;
  disabled: boolean;
}) {
  const countInGroup = perms.filter((p) => selected.has(p)).length;
  const allOn = countInGroup === perms.length;
  const someOn = countInGroup > 0 && countInGroup < perms.length;

  return (
    <div className="border border-border-subtle rounded-md overflow-hidden">
      <header className="flex items-center justify-between bg-bg-subtle px-3 py-2">
        <h4 className="text-2xs font-semibold uppercase tracking-wider text-fg">
          {groupName}{" "}
          <span className="text-fg-subtle normal-case">
            ({countInGroup}/{perms.length})
          </span>
        </h4>
        <label className="flex items-center gap-1.5 text-2xs text-fg-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={allOn}
            ref={(el) => {
              if (el) el.indeterminate = someOn;
            }}
            onChange={(e) => onToggleGroup(e.target.checked)}
            disabled={disabled}
          />
          {allOn ? "Quitar todos" : "Marcar todos"}
        </label>
      </header>
      <div className="p-2 flex flex-col gap-1">
        {perms.map((perm) => (
          <label
            key={perm}
            className={cn(
              "flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer hover:bg-bg-hover",
              disabled && "cursor-not-allowed opacity-60",
            )}
          >
            <input
              type="checkbox"
              checked={selected.has(perm)}
              onChange={(e) => onToggle(perm, e.target.checked)}
              disabled={disabled}
            />
            <code className="text-2xs text-fg font-mono">{perm}</code>
          </label>
        ))}
      </div>
    </div>
  );
}

function CreateRoleModal({
  onClose,
  onSubmit,
  submitting,
  groups,
}: {
  onClose: () => void;
  onSubmit: (data: { slug: string; name: string; description: string; permissions: string[] }) => void;
  submitting: boolean;
  groups: Record<string, string[]>;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Set<string>>(new Set());

  const allPerms = useMemo(() => Object.values(groups).flat(), [groups]);

  const toggle = (perm: string, on: boolean) => {
    setPermissions((s) => {
      const next = new Set(s);
      if (on) next.add(perm);
      else next.delete(perm);
      return next;
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[90] animate-fade-in" onClick={onClose} aria-hidden />
      <div className="fixed inset-0 z-[91] flex items-center justify-center p-4 pointer-events-none">
        <div className="nx-card pointer-events-auto w-full max-w-2xl p-5 animate-fade-in flex flex-col max-h-[90vh]">
          <header className="flex items-center justify-between mb-3">
            <h3 className="nx-h2">Nuevo rol custom</h3>
            <button onClick={onClose} className="nx-btn-ghost p-1">
              <X size={14} />
            </button>
          </header>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="nx-eyebrow block mb-1">Nombre</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ej. Analista Financiero"
                className="nx-input"
                maxLength={128}
                autoFocus
              />
            </div>
            <div>
              <label className="nx-eyebrow block mb-1">Slug (URL-safe)</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                placeholder="ej. analista_financiero"
                className="nx-input font-mono text-2xs"
                maxLength={64}
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="nx-eyebrow block mb-1">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Qué hace este rol (visible para futuros admins)"
              className="nx-input resize-y text-xs"
            />
          </div>

          <div className="flex items-center justify-between mb-2">
            <h4 className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">
              Permisos ({permissions.size} / {allPerms.length})
            </h4>
            <button
              onClick={() => setPermissions(new Set())}
              className="nx-btn-ghost text-2xs"
              type="button"
            >
              Limpiar
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-1">
            <div className="flex flex-col gap-3">
              {Object.entries(groups).map(([groupName, perms]) => (
                <PermissionGroup
                  key={groupName}
                  groupName={groupName}
                  perms={perms}
                  selected={permissions}
                  onToggle={(p, on) => toggle(p, on)}
                  onToggleGroup={(on) =>
                    setPermissions((s) => {
                      const next = new Set(s);
                      perms.forEach((p) => (on ? next.add(p) : next.delete(p)));
                      return next;
                    })
                  }
                  disabled={false}
                />
              ))}
            </div>
          </div>

          <footer className="flex justify-end gap-2 mt-4 pt-3 border-t border-border">
            <button onClick={onClose} className="nx-btn-secondary" disabled={submitting}>
              Cancelar
            </button>
            <button
              onClick={() =>
                onSubmit({
                  slug,
                  name,
                  description,
                  permissions: Array.from(permissions),
                })
              }
              disabled={submitting || !slug || !name || !/^[a-z][a-z0-9_-]{1,62}$/.test(slug)}
              className="nx-btn-primary"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Crear rol
            </button>
          </footer>
        </div>
      </div>
    </>
  );
}
